import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Shield, FileText, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Public signing page for the Founding Affiliate Agreement + NDA.
 *
 * Reached via `theailedger.ai/founder-affiliate/agreement/:token` — the URL
 * is generated by the admin and shared with the influencer manually.
 */
export default function FounderAffiliateAgreement() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [legalName, setLegalName] = useState('');
  const [acceptNda, setAcceptNda] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/founder-affiliate/agreement/${token}`);
        if (!cancelled) {
          setData(res.data);
          if (res.data.status === 'signed') setDone(true);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Agreement not found.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptNda || !acceptTerms) {
      toast.error('Please accept both the NDA and the agreement terms.');
      return;
    }
    if (legalName.trim().length < 2) {
      toast.error('Please enter your full legal name.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/founder-affiliate/agreement/${token}/sign`, {
        legal_name: legalName.trim(),
        accept_nda: acceptNda,
        accept_terms: acceptTerms,
      });
      setDone(true);
      toast.success('Agreement signed.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="agreement-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="agreement-error">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Agreement unavailable</h1>
          <p className="text-neutral-400 text-sm">{error}</p>
          <Link to="/" className="inline-block mt-6 text-[#00F0FF] hover:underline text-sm">Go home</Link>
        </div>
      </div>
    );
  }

  const aff = data.affiliate || {};

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4" data-testid="agreement-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-7 h-7 text-[#00F0FF]" />
          <div>
            <h1 className="text-2xl font-bold">Founding Affiliate Agreement</h1>
            <p className="text-sm text-neutral-500">Version {data.version} · theailedger.ai</p>
          </div>
        </div>

        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wide">Affiliate</div>
              <div className="font-semibold text-white">{aff.name}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wide">Email</div>
              <div className="font-semibold text-white">{aff.email}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wide">Branded URL</div>
              <code className="text-[#00F0FF] font-mono text-sm">theailedger.ai/{aff.slug}</code>
            </div>
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wide">Commission rate</div>
              <div className="font-semibold text-white">{aff.commission_rate}% (base) + stacked</div>
            </div>
          </div>
        </div>

        {done ? (
          <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-6 text-center" data-testid="agreement-signed">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-emerald-400 mb-1">Agreement signed</h2>
            <p className="text-sm text-emerald-200/80">
              {data.signed_name ? <>Signed by <b>{data.signed_name}</b>. </> : null}
              A copy has been emailed to the team. Welcome to the Founding Affiliate Program.
            </p>
          </div>
        ) : (
          <>
            <article className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mb-6 max-h-[420px] overflow-y-auto text-sm leading-relaxed text-neutral-200 whitespace-pre-wrap font-mono" data-testid="agreement-body">
              {data.body_markdown}
            </article>

            <form onSubmit={submit} className="space-y-4 bg-white/[0.03] border border-white/10 rounded-xl p-5">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#00F0FF]" /> Sign agreement
              </h3>
              <label className="block">
                <span className="block text-sm font-semibold mb-1.5">Full legal name *</span>
                <input value={legalName} onChange={(e) => setLegalName(e.target.value)}
                       required minLength={2} maxLength={120}
                       data-testid="legal-name-input"
                       placeholder="Jane Q. Public"
                       className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white" />
              </label>
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input type="checkbox" checked={acceptNda} onChange={(e) => setAcceptNda(e.target.checked)}
                       data-testid="accept-nda-checkbox"
                       className="mt-1 accent-[#00F0FF]" />
                <span className="text-neutral-200">
                  I agree to the <b>Confidentiality & NDA</b> obligations in <b>Section 6</b> of this Agreement.
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
                       data-testid="accept-terms-checkbox"
                       className="mt-1 accent-[#00F0FF]" />
                <span className="text-neutral-200">
                  I have read and agree to the full <b>Founding Affiliate Agreement</b> above, including the commission
                  schedule, lifetime-attribution rules, brand-use restrictions, and limitation of liability.
                </span>
              </label>
              <button type="submit" disabled={submitting || !acceptNda || !acceptTerms || legalName.trim().length < 2}
                      data-testid="sign-submit-btn"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#00F0FF]/90 disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Sign & accept
              </button>
              <p className="text-xs text-neutral-500 text-center">
                Your IP address and user-agent are recorded with the signature for legal record.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
